import { z } from 'zod';

export const OrderBy = z.object({
  key: z.string(), // ToDo: update this to a union of all the possible keys for each entity
  sortOrder: z.union([z.literal('asc'), z.literal('desc')]),
});
export type OrderBy = z.infer<typeof OrderBy>;

export const RelativeTimeFrame = z.enum([
  'hour',
  'day',
  'week',
  '14days',
  '30days',
  '2months',
]);
export type RelativeTimeFrame = z.infer<typeof RelativeTimeFrame>;

export const ExplicitTimeFrame = z.object({
  start: z.coerce.date(),
  end: z.coerce.date(),
});
export type ExplicitTimeFrame = z.infer<typeof ExplicitTimeFrame>;

export const TimeFrame = z.union([RelativeTimeFrame, ExplicitTimeFrame]);
export type TimeFrame = z.infer<typeof TimeFrame>;

export const GetSideBarQuery = z.object({
  environmentName: z.string().optional(),
});
export type GetSideBarQuery = z.infer<typeof GetSideBarQuery>;

export const BasicQuery = z.object({
  limit: z.coerce.number().int().positive().optional(),
  offset: z.coerce.number().int().nonnegative().optional(),
  orderBy: OrderBy.optional(), //override with the relevant SortableKeys
  searchTerms: z.array(z.string()).optional(),
  timeFrame: TimeFrame.optional(), // default is 'week'
  environmentName: z.string().optional(),
});
export type BasicQuery = z.infer<typeof BasicQuery>;

export const GetIssuesQuery = BasicQuery.extend({
  ruleId: z.string().optional(),
  onlyFixables: z.coerce.boolean().optional(), // include only issues that (as defined currently) have a fixitPrompt
  endpointId: z.coerce.number().optional(), // filter issues by specific endpoint
  serviceId: z.coerce.number().optional(), // filter issues by specific service
}); // we might want to extend this in the future
export type GetIssuesQuery = z.infer<typeof GetIssuesQuery>;

export const GetIssueQuery = z.object({
  environmentName: z.string().optional(),
});
export type GetIssueQuery = z.infer<typeof GetIssueQuery>;

export const EntityReference = z.object({
  id: z.number(),
  name: z.string(),
});
export type EntityReference = z.infer<typeof EntityReference>;

export const EntityState = z.union([
  z.literal('running'),
  z.literal('offline'),
  z.literal('unavailable'),
]);

export type EntityState = z.infer<typeof EntityState>;

export const SidebarData = z.object({
  totalServices: z.number(),
  totalFunctions: z.number(),
  totalEndpoints: z.number(),
  totalMessageQueues: z.number(),
});
export type SidebarData = z.infer<typeof SidebarData>;

export const EnvironmentRef = EntityReference.extend({
  totalFunctions: z.number(),
});
export type EnvironmentRef = z.infer<typeof EnvironmentRef>;

export const ErrorResponse = z.object({
  message: z.string(),
});
export type ErrorResponse = z.infer<typeof ErrorResponse>;

export const Tier = z.enum(['free', 'enterprise']);
export type Tier = z.infer<typeof Tier>;

export const Tenancy = z.enum(['single', 'multi']);
export type Tenancy = z.infer<typeof Tenancy>;

export const TierData = z.object({
  maxLiveSessions: z.number().optional(), // relevant for free tier only
  liveSessions: z.number().optional(), // relevant for free tier only
  throttledSessions: z.number().optional(),
});
export type TierData = z.infer<typeof TierData>;

export const GetAccountInfoResponse = z.object({
  companyName: z.string(),
  hasSession: z.boolean(),
  hasDeclarations: z.boolean(),
  hasMetrics: z.boolean(),
  tier: Tier,
  tierData: TierData.optional(),
  tenancy: Tenancy.optional(),
});

export type GetAccountInfoResponse = z.infer<typeof GetAccountInfoResponse>;

export const Metric = z.object({
  value: z.number(),
  trend: z.number().optional(), // percentage change
  symbol: z
    .union([
      z.literal('↑'),
      z.literal('↓'),
      z.literal('↗'),
      z.literal('↘'),
      z.literal('↑🔥'),
    ])
    .optional(),
});
export type Metric = z.infer<typeof Metric>;
const Deployment = z.object({
  functionVersionId: z.number(),
  timestamp: z.coerce.date(),
});
export const Deployments = z.array(Deployment);
export type Deployments = z.infer<typeof Deployments>;

export const GraphPoint = z.object({
  timestamp: z.coerce.date(),
  runs: z.number().nullable(),
  duration: z.number().nullable(), // this is the duration *average* but we should not change that to maintain backward compatibility for IDE
  errorRate: z.number().nullable(),
  durationP90: z.number().optional().nullable(),
  consumeDurationAvg: z.number().optional().nullable(),
  consumeDurationP90: z.number().optional().nullable(),
  msgCount: z.number().optional().nullable(),
  cost: z.number().optional().nullable(),
});
export type GraphPoint = z.infer<typeof GraphPoint>;

export const GraphsData = z.array(GraphPoint);
export type GraphsData = z.infer<typeof GraphsData>;

export const Runtime = z.enum(['python', 'nodejs']);
export type Runtime = z.infer<typeof Runtime>;

export const ThresholdFilter = z.object({
  operator: z.union([z.literal('gte'), z.literal('lte')]),
  value: z.coerce.number(),
});
export type ThresholdFilter = z.infer<typeof ThresholdFilter>;

export const Tag = z.enum(['job', 'pr', 'arch','subdomain','org_id','connector_id','operation_type']);
export type Tag = z.infer<typeof Tag>;

// Schema for individual tag entry (e.g., "job-frontend")
const TagEntrySchema = z
  .string()
  .refine((tag) => tag.includes('-'))
  .transform((tag) => {
    const dashIndex = tag.indexOf('-');
    const key = tag.substring(0, dashIndex);
    const value = tag.substring(dashIndex + 1);
    return { key, value };
  })
  .pipe(
    z.object({
      key: Tag, // Ensures key is one of the allowed Tag values
      value: z.string().min(1), // Ensures non-empty value
    }),
  );

// Schema for parsing an array of tags
export const TagRecordSchema = z.array(TagEntrySchema).transform((entries) =>
  entries.reduce(
    (acc, { key, value }) => {
      acc[key] = value;
      return acc;
    },
    {} as Record<string, string>,
  ),
);

export const HowDidYouHearAboutUsOption = z.enum([
  'social_network',
  'community_or_forum',
  'event',
  'search_engine',
  'podcast_or_webinar',
  'referral',
  'media',
  'other',
]);
export type HowDidYouHearAboutUsOption = z.infer<typeof HowDidYouHearAboutUsOption>;

export const SetupCompanyDto = z.object({
  companyName: z.string(),
  howDidYouHearAboutUs: HowDidYouHearAboutUsOption.optional(),
});
export type SetupCompanyDto = z.infer<typeof SetupCompanyDto>;
